import { createListCollection, Select } from '@skin-seoul/design-system';
import { CLASSNAME, detectBrowserLanguage, getLanguageCookie, setLanguageCookie } from '@skin-seoul/service';

import React, { useMemo } from 'react';
import { GOOGLE_TRANSLATE_LANGUAGE_OPTIONS, type LanguageOption } from './constants';

type TranslateLanguageSelectProps = {
  value?: string;
  onChange?: (language: string) => void;
  applyImmediately?: boolean;
};

export const TranslateLanguageSelect = ({ value, onChange, applyImmediately = true }: TranslateLanguageSelectProps) => {
  const items = useMemo<LanguageOption[]>(
    () => GOOGLE_TRANSLATE_LANGUAGE_OPTIONS.map((o: LanguageOption) => ({ value: o.value, label: o.label })),
    [],
  );
  const collection = useMemo(() => createListCollection<LanguageOption>({ items }), [items]);

  const browserLanguage = useMemo(() => detectBrowserLanguage(), []);

  const selected = value ?? getLanguageCookie() ?? browserLanguage;

  return (
    <Select.Root
      multiple={false}
      deselectable={false}
      collection={collection}
      onValueChange={({ value }) => {
        const lang = value[0];
        onChange?.(lang);
        if (applyImmediately) {
          setLanguageCookie(lang);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ss:user-language-change', { detail: lang }));
          }
        }
      }}
      value={[selected]}
      size="sm"
    >
      <Select.HiddenSelect />
      <Select.Label className={CLASSNAME.NOT_TRANSLATE} textStyle="paragraph.small" color="neutral.900">
        Language Setting
      </Select.Label>
      <Select.Control>
        <Select.Trigger
          height="40px"
          border="1px solid"
          borderColor="neutral.900"
          _hover={{
            borderColor: 'neutral.300',
          }}
        >
          <Select.ValueText className={CLASSNAME.NOT_TRANSLATE} textStyle="paragraph.small" color="neutral.900" />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator color="neutral.900" />
        </Select.IndicatorGroup>
      </Select.Control>
      <Select.Positioner>
        <Select.Content maxHeight="50dvh" boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.1)">
          {collection.items.map((country: LanguageOption) => (
            <Select.Item
              padding="12px 8px"
              key={country.value}
              item={country}
              className={CLASSNAME.NOT_TRANSLATE}
              _selected={{
                backgroundColor: 'neutral.100/60',
              }}
            >
              {country.label}
              <Select.ItemIndicator />
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  );
};

export default TranslateLanguageSelect;
